// src/features/procedures/components/EditableStepCard.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { STEP_TYPES, getDefaultsForStepType } from '../config/stepTypeConfig';
import { getStepTypeConfig, getValidStepTypeValues } from '../config/stepTypes';
import StepOptionFields from './stepOptions';
import TableGridStep from './steps/TableGridStep';

export default function EditableStepCard({
  step,
  stepNumber,
  onUpdate,
  onDelete,
  disabled,
  isExpanded,
  onToggleExpand,
}) {
  const { t } = useTranslation(['procedures', 'common']);
  const [editData, setEditData] = useState({
    step_type: step.step_type,
    label: step.label,
    description: step.description || '',
    is_required: step.is_required,
    ai_model: step.ai_model || '',
    validation_rules: step.validation_rules || null,
    options: step.options || null,
  });

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.step_id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleStepTypeChange = (newType) => {
    const defaults = getDefaultsForStepType(newType);
    const updated = {
      ...editData,
      ...defaults,
    };
    setEditData(updated);
    onUpdate(updated);
  };

  const handleDataChange = (updates) => {
    const updated = { ...editData, ...updates };
    setEditData(updated);
    onUpdate(updated);
  };

  const handleFieldChange = (field, value) => {
    const updated = { ...editData, [field]: value };
    setEditData(updated);
    onUpdate(updated);
  };

  const StepPreviewComponent = getStepTypeConfig(editData.step_type)?.component || TableGridStep;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isExpanded ? 3 : 1}
      sx={{
        p: 0,
        opacity: isDragging ? 0.5 : 1,
        bgcolor: isDragging ? 'action.hover' : 'background.paper',
        transition: 'all 0.2s',
        border: isExpanded ? 2 : 0,
        borderColor: 'primary.main',
        cursor: isExpanded ? 'default' : 'pointer',
      }}
      onClick={() => !isExpanded && onToggleExpand()}
    >
      {/* Collapsed View */}
      {!isExpanded && (
        <Box sx={{ p: 2 }} display="flex" alignItems="center" gap={2}>
          {/* Drag Handle */}
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: disabled ? 'not-allowed' : 'grab',
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              touchAction: 'none',
              '&:active': {
                cursor: disabled ? 'not-allowed' : 'grabbing',
              },
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIcon />
          </Box>

          {/* Step Number */}
          <Typography
            variant="body2"
            sx={{
              minWidth: 24,
              color: 'text.secondary',
              fontWeight: 'medium',
            }}
          >
            {stepNumber}.
          </Typography>

          {/* Step Info */}
          <Box flex={1}>
            <Typography variant="body1" fontWeight="medium">
              {step.label || 'Untitled Question'}
            </Typography>
            {step.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {step.description}
              </Typography>
            )}
            {/* Preview */}
            <Box sx={{ mt: 1, pointerEvents: 'none' }}>
              <StepPreviewComponent
                step={editData}
                value=""
                onChange={() => {}}
                disabled={false}
                preview={true}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Expanded View (Edit Mode) */}
      {isExpanded && (
        <Box sx={{ p: 3 }} onClick={(e) => e.stopPropagation()}>
          {/* Top Bar with Drag Handle */}
          <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
            {/* Drag Handle */}
            <Box
              {...attributes}
              {...listeners}
              sx={{
                cursor: disabled ? 'not-allowed' : 'grab',
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                touchAction: 'none',
                pt: 1,
                '&:active': {
                  cursor: disabled ? 'not-allowed' : 'grabbing',
                },
              }}
            >
              <DragIcon />
            </Box>

            {/* Label (Left) and Type (Right) */}
            <Box flex={1} display="flex" gap={2}>
              <TextField
                fullWidth
                label={t('procedures:labels.name')}
                value={editData.label}
                onChange={(e) => handleFieldChange('label', e.target.value)}
                required
                placeholder="Question"
                sx={{ flex: 2 }}
              />
              <TextField
                select
                label={t('procedures:labels.type')}
                value={editData.step_type}
                onChange={(e) => handleStepTypeChange(e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              >
                {STEP_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          {/* Description */}
          <Box sx={{ pl: 6 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('procedures:labels.description')}
              value={editData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Add a description (optional)"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Step Options */}
            <StepOptionFields
              stepType={editData.step_type}
              data={editData}
              onDataChange={handleDataChange}
            />

            <Divider sx={{ my: 2 }} />

            {/* Bottom Bar - Required Toggle and Actions */}
            <Box display="flex" justifyContent="end" alignItems="center">
              <Box display="flex" gap={1}>
                <IconButton
                  size="small"
                  onClick={() => {
                    // TODO: Implement copy functionality
                  }}
                  disabled={disabled}
                  title="Duplicate"
                >
                  <CopyIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={onDelete}
                  disabled={disabled}
                  title="Delete"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Box backgroundColor={"divider"} width={"2px"} height={"25px"} mr={3} ml={2} borderRadius={"2px"} />
              <FormControlLabel
                control={
                  <Switch
                    checked={editData.is_required}
                    onChange={(e) => handleFieldChange('is_required', e.target.checked)}
                  />
                }
                label={t('procedures:labels.required')}
              />

            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
